import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, X, CheckCheck, Info, AlertTriangle, CheckCircle, AlertCircle, Clock, Trash2 } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { performNotificationClickNavigation } from '../../utils/notificationNavigation';
import {
  isDashboardRoute,
  PROFILE_COACH_MARK_AUTO_HIDE_MS,
  shouldShowProfileCompletionCoachMark,
} from '../../utils/profileCompletionReminder';

/**
 * Body line for the bell list: existing message plus explicit ticket number and numeric ticket ID when present.
 */
function formatNotificationBodyPreview(notification) {
  const base = String(notification?.body || notification?.message || '').trim();
  const text = base || '—';

  const ticketId =
    notification?.ticketId ??
    notification?.metadata?.ticketId ??
    notification?.data?.ticketId ??
    null;

  const ticketNumber =
    notification?.ticketNumber ??
    notification?.metadata?.ticketNumber ??
    notification?.data?.ticketNumber ??
    null;

  const parts = [];
  if (ticketNumber != null && String(ticketNumber).trim() !== '') {
    parts.push(`Ticket #${String(ticketNumber).trim()}`);
  }
  if (ticketId != null && String(ticketId).trim() !== '') {
    parts.push(`Ticket ID ${String(ticketId).trim()}`);
  }

  if (parts.length === 0) {
    return text;
  }

  return `${text} · ${parts.join(' · ')}`;
}

/**
 * NotificationBell - Bell icon with badge and dropdown
 * Shows notification count and list of notifications
 *
 * @param {object} [props]
 * @param {Array<object>} [props.menuData] - Dynamic menu from Home (optional; falls back to sessionStorage cache)
 */
const NotificationBell = ({ menuData: menuDataProp } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showCoachMark, setShowCoachMark] = useState(false);
  const dropdownRef = useRef(null);
  const coachAutoHideTimerRef = useRef(null);

  const isOnDashboard = useMemo(
    () => isDashboardRoute(location.pathname, menuDataProp),
    [location.pathname, menuDataProp]
  );

  const {
    notifications,
    unreadCount,
    markAllAsRead,
    dismissAllFromPanel,
    dismissProfileCompletionReminder,
    getNotificationIcon,
  } = useNotifications();

  const visibleNotifications = (notifications || []).slice(0, 30);
  const panelUnreadCount = visibleNotifications.filter(
    (notification) => !notification.read && !notification.isRead
  ).length;

  const hideCoachMark = useCallback(() => {
    setShowCoachMark(false);
    if (coachAutoHideTimerRef.current) {
      clearTimeout(coachAutoHideTimerRef.current);
      coachAutoHideTimerRef.current = null;
    }
  }, []);

  const tryShowCoachMark = useCallback(() => {
    if (isOnDashboard && shouldShowProfileCompletionCoachMark()) {
      setShowCoachMark(true);
      return;
    }
    setShowCoachMark(false);
  }, [isOnDashboard]);

  useEffect(() => {
    if (!isOnDashboard) {
      setShowCoachMark(false);
      return undefined;
    }

    const timer = setTimeout(tryShowCoachMark, 300);

    const handleUserDetailsUpdated = () => {
      tryShowCoachMark();
    };

    window.addEventListener('hd:user-details-updated', handleUserDetailsUpdated);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('hd:user-details-updated', handleUserDetailsUpdated);
    };
  }, [isOnDashboard, location.pathname, tryShowCoachMark]);

  useEffect(() => {
    if (!showCoachMark) {
      return undefined;
    }

    coachAutoHideTimerRef.current = setTimeout(() => {
      hideCoachMark();
    }, PROFILE_COACH_MARK_AUTO_HIDE_MS);

    return () => {
      if (coachAutoHideTimerRef.current) {
        clearTimeout(coachAutoHideTimerRef.current);
        coachAutoHideTimerRef.current = null;
      }
    };
  }, [showCoachMark, hideCoachMark]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format timestamp to relative time
  const formatTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Get icon component based on notification type
  const getIcon = (type) => {
    const iconType = getNotificationIcon(type);
    switch (iconType) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'info':
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (notification?.notificationType === 'profile_completion' || notification?.type === 'profile_completion') {
      return;
    }
    const { navigated } = performNotificationClickNavigation(notification, menuDataProp);
    if (navigated) {
      setIsOpen(false);
    }
  };

  const handleCoachMarkNavigate = (e) => {
    e.stopPropagation();
    hideCoachMark();
    navigate('/profile');
  };

  const handleUpdateProfile = (e) => {
    e.stopPropagation();
    const profilePath =
      notifications.find((n) => n.notificationType === 'profile_completion')?.metadata?.profilePath ||
      '/profile';
    navigate(profilePath);
    setIsOpen(false);
  };

  const handleProfileReminderLater = (e) => {
    e.stopPropagation();
    dismissProfileCompletionReminder();
    setIsOpen(false);
  };

  // Handle mark all as read
  const handleMarkAllAsRead = (e) => {
    e.stopPropagation();
    markAllAsRead();
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    dismissAllFromPanel();
  };

  const handleBellClick = () => {
    if (showCoachMark) {
      hideCoachMark();
    }
    setIsOpen((open) => !open);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={handleBellClick}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        title="Notifications"
        aria-label={`Notifications ${panelUnreadCount > 0 ? `(${panelUnreadCount} unread)` : ''}`}
      >
        <Bell size={20} />

        {/* Badge Count */}
        {panelUnreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#e53e3e] text-xs font-bold text-white animate-pulse">
            {panelUnreadCount > 9 ? '9+' : panelUnreadCount}
          </span>
        )}
      </button>

      {showCoachMark && !isOpen && (
        <div
          role="status"
          className="absolute top-[calc(100%+10px)] right-0 z-[60] w-96 rounded-md bg-[#2b6cb0] p-4 shadow-lg"
        >
          <div
            className="absolute -top-2 right-6 h-4 w-4 rotate-45 bg-[#2b6cb0]"
            aria-hidden="true"
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              hideCoachMark();
            }}
            className="absolute top-3 right-3 rounded p-0.5 text-white/90 hover:text-white"
            aria-label="Dismiss hint"
          >
            <X size={14} />
          </button>
          <button
            type="button"
            onClick={handleCoachMarkNavigate}
            className="block w-full pr-8 text-left text-sm leading-6 text-white transition-opacity hover:opacity-90 cursor-pointer"
          >
            <span className="font-bold text-base">Complete your profile:</span>{' '}
            update your mobile number to ensure secure access.
          </button>
        </div>
      )}

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[480px] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {visibleNotifications.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="text-xs text-gray-600 hover:text-gray-800 font-medium flex items-center gap-1"
                >
                  <Trash2 size={14} />
                  Clear all
                </button>
              )}
              {panelUnreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  <CheckCheck size={14} />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                aria-label="Close notifications"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto flex-1">
            {visibleNotifications.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {visibleNotifications.map((notification) => {
                  const isProfileReminder =
                    notification.notificationType === 'profile_completion' ||
                    notification.type === 'profile_completion';

                  return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      isProfileReminder ? 'cursor-default' : 'cursor-pointer'
                    } ${!notification.read ? 'bg-blue-50/50' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-0.5">
                        {getIcon(notification.notificationType || notification.type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={`text-sm font-medium truncate ${
                              !notification.read ? 'text-gray-900' : 'text-gray-700'
                            }`}
                          >
                            {notification.title}
                          </p>
                          {/* Unread indicator */}
                          {!notification.read && (
                            <span className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {/* {formatNotificationBodyPreview(notification)} */}
                          {notification.body || notification.message}
                        </p>
                        {/* Priority indicator */}
                        {notification.priority && notification.priority !== 'normal' && (
                          <span
                            className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${
                              notification.priority === 'urgent'
                                ? 'bg-red-100 text-red-700'
                                : notification.priority === 'high'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {notification.priority}
                          </span>
                        )}
                        <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                          <Clock size={12} />
                          {formatTime(notification.timestamp)}
                        </p>
                        {isProfileReminder && (
                          <div className="mt-3 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={handleUpdateProfile}
                              className="text-xs font-medium px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                            >
                              Update Profile
                            </button>
                            <button
                              type="button"
                              onClick={handleProfileReminderLater}
                              className="text-xs font-medium px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                            >
                              Later
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No notifications yet</p>
                <p className="text-xs text-gray-400 mt-1">
                  You'll see notifications here when you receive them
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          {visibleNotifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  navigate('/agent/notifications');
                  setIsOpen(false);
                }}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View All Notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
